import { createRoot } from "react-dom/client";
import { MemoryRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "store";
import { AppRoutes } from "AppRouter";
import { ExportPortalProvider } from "./exportPortal";
import { createExportReadiness, ExportReadinessProvider } from "./exportReadiness";

/**
 * Monta una ruta de exportacion (impresion o Excel) DENTRO de la app ya cargada
 * (mismo store/auth/JS), en un contenedor oculto, y resuelve con lo que el
 * `waitFor` produzca cuando esa vista termine de renderizar.
 *
 * Es la mecanica compartida por `renderRouteToHtml` (serializa el DOM para el PDF)
 * y `renderRouteToExcel` (captura los bytes del libro de Excel). Lo unico que
 * cambia entre ambos es como se detecta el "listo" y que se devuelve.
 *
 * Reemplaza al antiguo enfoque de iframe que navegaba la ruta (segundo boot de la
 * app + throttling de pestaña en segundo plano). Mantiene una sola fuente de
 * verdad: usa el mismo arbol de rutas (`AppRoutes`) y, por tanto, el mismo
 * `TenantTemplateRouterCore` multi-plantilla por tenant.
 */

export const EXPORT_PDF_QUERY_PARAM = "export";
export const EXPORT_ID_PDF_QUERY_PARAM = "exportId";

export type RoutePortalContext = {
    /** Identificador unico de esta exportacion; viaja en la query de la ruta montada. */
    exportId: string,
    /** Contenedor oculto donde vive el render; sigue vivo mientras corre el `waitFor`. */
    container: HTMLElement,
    origin: string,
    /** Registra listeners/suscripciones a limpiar tanto al resolver como al fallar. */
    registerCleanup: (fn: () => void) => void
}

export type RoutePortalOptions = {
    timeoutMs?: number
}

export function renderRouteInPortal<T>(
    path: string,
    waitFor: (ctx: RoutePortalContext) => Promise<T>,
    opts: RoutePortalOptions = {}
): Promise<T> {
    const { timeoutMs = 20000 } = opts;

    const exportId = `export-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Construir la entrada del MemoryRouter con los flags de export.
    const url = new URL(path, window.location.origin);
    if (!url.searchParams.has(EXPORT_PDF_QUERY_PARAM)) {
        url.searchParams.set(EXPORT_PDF_QUERY_PARAM, "1");
    }
    url.searchParams.set(EXPORT_ID_PDF_QUERY_PARAM, exportId);
    const memoryEntry = url.pathname + url.search;

    // Contenedor oculto fuera de viewport. Solo se usa para serializar el DOM;
    // el layout real del PDF lo realiza Chromium en el lambda.
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.left = "-99999px";
    container.style.top = "-99999px";
    container.style.width = "1024px";
    container.style.visibility = "hidden";
    container.setAttribute("aria-hidden", "true");
    document.body.appendChild(container);

    // `Page` (via el wrapper de rutas) escribe document.title; lo restauramos
    // para no alterar el titulo de la pestaña del usuario durante la generacion.
    const previousTitle = document.title;

    // Los componentes Print importan CSS GLOBAL (reset Meyer, base, generic.scss)
    // como efecto secundario. En la app real esas vistas se abren en otra pestaña
    // (window.open), asi que ese CSS nunca convive con la app principal. Aqui, al
    // renderizar en el mismo documento, Vite inyectaria ese CSS en el <head>
    // compartido y romperia la app principal. Observamos el <head> y
    // deshabilitamos (sin removerlas, para no romper el cache de modulos de Vite)
    // las hojas de estilo inyectadas durante el render: la app principal no se ve
    // afectada, pero su texto sigue intacto para serializarlo hacia el PDF.
    const knownHeadNodes = new Set<Node>(Array.from(document.head.childNodes));
    const disableInjectedStyle = (node: HTMLStyleElement | HTMLLinkElement) => {
        try {
            (node as HTMLLinkElement).disabled = true;
        } catch {
            /* <style> no expone .disabled; se deshabilita via .sheet */
        }
        const disableSheet = () => {
            try {
                if (node.sheet) node.sheet.disabled = true;
            } catch {
                /* ignore */
            }
        };
        disableSheet();
        node.addEventListener("load", disableSheet, { once: true });
    };
    const headObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (knownHeadNodes.has(node)) return;
                const isStyle = node.nodeName === "STYLE";
                const isStylesheetLink =
                    node.nodeName === "LINK" && (node as HTMLLinkElement).rel === "stylesheet";
                if (isStyle || isStylesheetLink) {
                    disableInjectedStyle(node as HTMLStyleElement | HTMLLinkElement);
                }
            });
        }
    });
    headObserver.observe(document.head, { childList: true });

    const root = createRoot(container);
    const extraCleanups: Array<() => void> = [];

    // Registro del trabajo asincrono de esta exportacion. Es por portal (no un
    // singleton de modulo) para que dos exportaciones no se lean el contador entre si.
    const readiness = createExportReadiness();

    return new Promise<T>((resolve, reject) => {
        let done = false;

        const cleanup = () => {
            headObserver.disconnect();
            extraCleanups.forEach((fn) => {
                try {
                    fn();
                } catch {
                    /* ignore */
                }
            });
            window.clearTimeout(timeoutId);
            // Desmontar fuera del ciclo actual para evitar warnings de React al
            // hacerlo dentro del propio handler de evento.
            setTimeout(() => {
                root.unmount();
                container.parentElement?.removeChild(container);
                document.title = previousTitle;
            }, 0);
        };

        const timeoutId = window.setTimeout(() => {
            if (done) return;
            done = true;
            cleanup();
            reject(new Error(`Timeout esperando el render de la ruta ${memoryEntry}`));
        }, timeoutMs);

        // El `waitFor` se engancha ANTES de renderizar para no perderse la señal de
        // "listo" de vistas que resuelven de inmediato.
        waitFor({
            exportId,
            container,
            origin: url.origin,
            registerCleanup: (fn: () => void) => extraCleanups.push(fn),
        })
            .then((value) => {
                if (done) return;
                done = true;
                cleanup();
                resolve(value);
            })
            .catch((err) => {
                if (done) return;
                done = true;
                cleanup();
                reject(err);
            });

        root.render(
            <ExportPortalProvider value={true}>
                <ExportReadinessProvider value={readiness}>
                    <Provider store={store}>
                        <MemoryRouter initialEntries={[memoryEntry]}>
                            <AppRoutes />
                        </MemoryRouter>
                    </Provider>
                </ExportReadinessProvider>
            </ExportPortalProvider>
        );
    });
}
